<template>
    <div>
        <b-table
            :empty-text="$t('there_are_no_records')"
            bordered
            striped
            responsive
            :items="items"
            :fields="fields"
            show-empty
            class="mb-5 table-dropdown no-scrollbar border rounded"
        >
            <template #cell(buttons)="row">
                <b-dropdown variant="link btn-sm" boundary="window" no-caret class="compact-dropdown"
                            v-if="['waiting_student_approve', 'waiting_documents'].includes(row.item.status)">
                    <template #button-content>
                        <i class="ri-more-fill text-muted" style="font-size: 1.2rem;"></i>
                    </template>
                    <div>
                        <b-dropdown-item
                            v-if="row.item.status == 'waiting_student_approve'"
                            @click="showTransferredCourses(row.item)">
                            <i class="ri-eye-line align-middle mr-2 text-muted"></i>
                            {{ $t('show') }}
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="row.item.status == 'waiting_documents'"
                            @click="updateDocument()">
                            <i class="ri-file-upload-line align-middle mr-2 text-muted"></i>
                            {{ $t('update_document') }}
                        </b-dropdown-item>
                    </div>
                </b-dropdown>
            </template>
        </b-table>

        <CommonModal id="transferredCoursesModal" size="xl" :onHideOnlyX="true" :footer="false"
                     :footer-class="'justify-content-center'">
            <template v-slot:CommonModalContent>
                <b-row>
                    <b-col cols="12">
                        <b-alert variant="secondary" show>{{ studentCourseTransferText }}</b-alert>
                    </b-col>
                </b-row>
                <div>
                    <div v-if="slots != null">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>{{ $t('slot_type').toUpperCase() }}</th>
                                    <th class="width-100">{{ $t('course_code').toUpperCase() }}</th>
                                    <th>{{ $t('course_name').toUpperCase() + ' / ' + $t('elective_pool').toUpperCase() }}</th>
                                    <th class="w-40">{{ $t('credit').toUpperCase() }}</th>
                                    <th class="w-40">{{ $t('ects_credit').toUpperCase() }}</th>
                                    <th class="w-40">{{ $t('semester').toUpperCase() }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="slot in slots" :key="slot.id">
                                    <td>{{ slot.slot_type ? getLocaleText(slot.slot_type, 'name') : "" }}</td>
                                    <td>{{ slot.course ? slot.course?.code : '' }}</td>
                                    <td>{{
                                            slot.course ? getLocaleText(slot.course, 'name') : (slot.pool ? getLocaleText(slot.pool, 'name') : '')
                                        }}
                                    </td>
                                    <td>{{ slot.credit }}</td>
                                    <td>{{ slot.ects_credit }}</td>
                                    <td>{{ slot.semester }}</td>
                                    <td>
                                        <div v-if="slot.transferred_courses">
                                            <p v-for="course in slot.transferred_courses" :key="course.id">
                                                <span>
                                                    <span v-if="course.university_course && course.university_course.university">
                                                        <b>{{ $t('university') }}:</b>
                                                        {{ course.university_course.university?.name }}
                                                    </span>
                                                    <br>
                                                    <b>{{ $t('course_code') }}:</b> {{ course.university_course?.code }}
                                                    <b>{{ $t('course_name') }}:</b> {{ course.university_course?.name }}
                                                    <b>{{ $t('grade') }}:</b> {{ course.letter_grade }} ({{ course.converted_letter_grade }})
                                                    <b>{{ $t('credit') }}:</b> {{ course.credit }}
                                                    <b>{{ $t('ects_credit') }}:</b> {{ course.ects_credit }}
                                                </span>
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <ValidationObserver ref="studentApproveForm">
                        <div class="row">
                            <div class="col-12">
                                <ValidationProvider name="status" rules="required" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('status')">
                                        <multi-selectbox :multiple="false"
                                                         :options="[{value:'approved', text: $t('i_approve')}, {value:'declined', text: $t('i_not_approve')}]"
                                                         v-model="studentApproveForm.student_approve_status"
                                                         :state="errors[0] ? false : (valid ? true : null)">
                                        </multi-selectbox>
                                        <b-form-invalid-feedback v-if="errors[0]"
                                                                 v-html="errors[0]"></b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>

                            <div class="col-12" v-if="studentApproveForm.student_approve_status == 'declined'">
                                <ValidationProvider name="explanation" rules="required" v-slot="{valid, errors}">
                                    <b-form-group :label="$t('explanation')">
                                        <b-form-textarea
                                            v-model="studentApproveForm.student_explanation"
                                            :state="errors[0] ? false : (valid ? true : null)">
                                        </b-form-textarea>
                                         <small class="text-muted d-block mt-1">
                                            {{ $t('character_limit_500') }}
                                        </small>
                                        <b-form-invalid-feedback v-if="errors[0]"
                                                                 v-html="errors[0]"></b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>

                        </div>
                        <div class="row">
                            <div class="col-12">
                                <b-form-group>
                                    <b-button variant="primary"
                                              @click="saveCourseTransferStatus">
                                        {{ $t('save') }}
                                    </b-button>
                                </b-form-group>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
import CourseTransferService from "@/services/CourseTransferService";
import ConstantService from "@/services/ConstantService";
import CommonModal from "@/components/elements/CommonModal";
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox";
import { ValidationObserver, ValidationProvider } from "vee-validate";

/**
 * Course Transfer Tab Component
 */
export default {
    name: 'CourseTransferTab',
    components: {
        CommonModal,
        MultiSelectbox,
        ValidationObserver,
        ValidationProvider
    },
    data() {
        return {
            items: [],
            fields: [
                { key: 'buttons', label: '', thStyle: { width: '50px' }, tdClass: 'text-center' },
                {
                    key: this.getLocaleField('program_name'),
                    label: this.toUpperCase('program'),
                },
                { key: 'created_at', label: this.toUpperCase('date') },
                { key: 'calculated_cgpa', label: this.toUpperCase('calculated_cgpa') },
                { key: 'active_approver', label: this.toUpperCase('at_which_unit') },
                { key: this.getLocaleField('status_name'), label: this.toUpperCase('status') },
                {
                    key: this.getLocaleField('steps[0].reason'),
                    label: this.toUpperCase(this.$t('description')),
                },
            ],
            slots: null,
            selectedCourseTransfer: null,
            studentApproveForm: {
                student_approve_status: null,
                student_explanation: null
            },
            studentCourseTransferText: null,
        };
    },
    methods: {
        fetchData() {
            CourseTransferService.studentRequests()
                .then(response => {
                    this.items = response.data.data;
                    this.items.forEach(item => {
                        if (item.active_step && item.active_step.approvers) {
                            let arr = [];
                            item.active_step.approvers.forEach(approver => {
                                arr.push(this.getLocaleText(approver, 'explanation'));
                            });
                            item.active_approver = arr.join(', ');
                        }
                    });
                })
                .catch(e => {
                    this.showErrors(e);
                });
        },
        showTransferredCourses(row) {
            CourseTransferService.studentTransferredCourses(row.id)
                .then(response => {
                    this.slots = response.data.data;
                    this.$bvModal.show('transferredCoursesModal');
                });
            this.selectedCourseTransfer = row;
        },
        updateDocument() {
            this.$router.push("/course/transfers/form");
        },
        async saveCourseTransferStatus() {
            const isValid = await this.$refs.studentApproveForm.validate();
            if (isValid) {
                CourseTransferService.studentApprove(this.selectedCourseTransfer.id, this.studentApproveForm)
                    .then(response => {
                        this.$toast.success(this.$t('proccess_success'));
                        this.$bvModal.hide('transferredCoursesModal');
                        this.fetchData();
                    })
                    .catch((e) => {
                        if (e.data && e.data.message) {
                            this.$toast.error(this.$t("api." + e.data.message));
                        }
                    });
            }
        },
        async getCourseTransferText() {
            const config = {
                params: {
                    key: this.$i18n.locale == 'tr' ? "course_transfers_student_warning_message_tr" : "course_transfers_student_warning_message_en"
                },
            };
            ConstantService.getValue(config)
                .then(response => {
                    this.studentCourseTransferText = response.data.data;
                })
                .catch(err => {
                    this.showErrors(err);
                });
        }
    },
    created() {
        this.fetchData();
        this.getCourseTransferText();
    }
};
</script>

<style scoped>
.compact-dropdown >>> .dropdown-toggle {
    padding: 0;
    line-height: 1;
}
</style>
